// PostHog-backed marketing analytics for the landing site. Kept as the single
// file that touches `posthog-js`; the rest of the site calls capturePageview /
// captureEvent and never sees the SDK.
//
// This mirrors apps/client/src/shared/lib/analytics.ts governance, with three
// deliberate differences that fit a public marketing site:
//   - autocapture is ON. The landing has no financial DOM (no balances,
//     tickers, order sizes), so the trading-app reason for disabling it does
//     not apply, and autocapture gives us CTA/button/scroll attribution for
//     free. Note: PostHog autocapture never records typed <input> values, so
//     the waitlist / referral email fields are not egressed.
//   - session replay records page TEXT here. The app records replay too, but
//     with every text node masked (its DOM carries balances/orders); this site
//     has no financial DOM, so unmasked text is what makes a marketing replay
//     readable. All inputs are masked either way, so the email fields are never
//     recorded. It is a separate PostHog SKU with its own quota, so bound cost
//     with a sampling % in PostHog project settings (Replay).
//   - We never identify. Marketing visitors stay anonymous (no person
//     profiles); the waitlist -> account join is done server-side in Postgres
//     against a hashed-email bridge, never by egressing an email to PostHog.
//
// Governance shared with the app:
//   - GPC (Global Privacy Control) and Do-Not-Track are honored as opt-outs.
//   - EU/EEA/UK are region-gated OFF upstream: the landing Worker sets
//     `analyticsAllowed=false` from `request.cf.country`, folded into `enabled`.
//   - A local browser origin never initializes, so `vite preview` / a local
//     build against the prod config can't emit real events.
//   - A missing key keeps the whole surface a no-op (like a missing Sentry DSN).

import type { LandingAnalyticsEvent } from "./analytics-events";
import { scrubEventProps } from "./analytics-events";

export type AnalyticsConfig = {
  apiKey?: string;
  host?: string;
  // Folds in the region gate (analyticsAllowed) and key presence upstream.
  enabled?: boolean;
  // In-app browser source (wechat/tiktok/...) detected server-side by the
  // Worker from the UA. Registered as an attribution super property below.
  inappSource?: string;
};

type PosthogShape = {
  init: (token: string, options: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  register: (properties: Record<string, unknown>) => void;
  register_once: (properties: Record<string, unknown>) => void;
};

const DEFAULT_HOST = "https://us.i.posthog.com";

let initialized = false;
let enabled = false;
let client: PosthogShape | null = null;

function sanitizeAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value);
    // UTM values are already promoted to dedicated PostHog properties. Keep
    // raw query/hash values—including referral lookup emails/codes—out of
    // current-url, referrer, autocapture, and replay event envelopes.
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return value.split(/[?#]/, 1)[0] ?? "/";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function sanitizeLandingCapturedNetworkRequest(
  request: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...request };
  // PostHog's current CapturedNetworkRequest shape stores the URL in `name`;
  // retain `url` handling for the compatibility shape used by older replay
  // clients. This callback also powers rrweb's initial document href masker.
  if (typeof next.name === "string") next.name = sanitizeAnalyticsUrl(next.name);
  if (typeof next.url === "string") next.url = sanitizeAnalyticsUrl(next.url);
  return next;
}

export function sanitizeLandingAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...properties };
  for (const key of ["$current_url", "$initial_current_url", "$referrer", "$initial_referrer"]) {
    if (typeof next[key] === "string") next[key] = sanitizeAnalyticsUrl(next[key]);
  }

  // Defense in depth for replay envelopes. The replay recorder applies the
  // session URL callback before producing snapshots, but before_send may also
  // see a raw or externally constructed snapshot.
  if (isRecord(next.$snapshot_data) && isRecord(next.$snapshot_data.data)) {
    const data = { ...next.$snapshot_data.data };
    if (typeof data.href === "string") data.href = sanitizeAnalyticsUrl(data.href);
    next.$snapshot_data = { ...next.$snapshot_data, data };
  }

  // Heatmap buffers use the current URL as object keys rather than one of the
  // standard PostHog URL properties. Strip legacy referral lookups here too.
  if (isRecord(next.$heatmap_data)) {
    next.$heatmap_data = Object.fromEntries(
      Object.entries(next.$heatmap_data).map(([url, samples]) => [
        sanitizeAnalyticsUrl(url),
        samples,
      ]),
    );
  }

  // Web-vitals buffers their own nested current URL instead of inheriting the
  // event-level property.
  for (const [key, value] of Object.entries(next)) {
    if (!key.startsWith("$web_vitals_") || !key.endsWith("_event") || !isRecord(value)) {
      continue;
    }
    const metric = { ...value };
    if (typeof metric.$current_url === "string") {
      metric.$current_url = sanitizeAnalyticsUrl(metric.$current_url);
    }
    next[key] = metric;
  }

  // Autocapture stores link destinations on its nested element chain. Legacy
  // referral lookup values must not survive there even if a link was rendered
  // before React replaced the URL.
  if (Array.isArray(next.$elements)) {
    next.$elements = next.$elements.map((element) => {
      if (!isRecord(element)) return element;
      const sanitized = { ...element };
      for (const key of ["href", "attr__href"]) {
        if (typeof sanitized[key] === "string") {
          sanitized[key] = sanitizeAnalyticsUrl(sanitized[key]);
        }
      }
      return sanitized;
    });
  }
  if (typeof next.$external_click_url === "string") {
    next.$external_click_url = sanitizeAnalyticsUrl(next.$external_click_url);
  }
  return next;
}

function hasDom(): boolean {
  return typeof document !== "undefined" && typeof window !== "undefined";
}

// Honor GPC and the legacy DNT signal, matching ADR-0021 and the product app.
function browserSignalsOptOut(): boolean {
  if (!hasDom()) return false;
  const nav = navigator as unknown as {
    globalPrivacyControl?: boolean;
    doNotTrack?: string | null;
    msDoNotTrack?: string | null;
  };
  const legacyWindow = window as unknown as { doNotTrack?: string | null };
  return (
    nav.globalPrivacyControl === true ||
    nav.doNotTrack === "1" ||
    nav.msDoNotTrack === "1" ||
    legacyWindow.doNotTrack === "1"
  );
}

// A local origin must never emit real events even when the prod key is present.
function originAllowsAnalytics(): boolean {
  if (!hasDom()) return false;
  const host = window.location?.hostname ?? "";
  if (!host) return false;
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host === "0.0.0.0" ||
    host.endsWith(".local")
  ) {
    return false;
  }
  return true;
}

export async function initAnalytics(config: AnalyticsConfig): Promise<void> {
  if (initialized) return;
  initialized = true;
  enabled =
    (config.enabled ?? Boolean(config.apiKey)) &&
    hasDom() &&
    originAllowsAnalytics() &&
    !browserSignalsOptOut();
  if (!enabled || !config.apiKey) {
    enabled = false;
    return;
  }
  try {
    const mod = (await import("posthog-js")) as unknown as { default: PosthogShape };
    const ph = mod.default;
    ph.init(config.apiKey, {
      api_host: config.host && config.host.length > 0 ? config.host : DEFAULT_HOST,
      // SPA routing: we emit `$pageview` manually on React-Router changes.
      capture_pageview: false,
      // Capture `$pageleave` for real time-on-page / bounce quality (explicit
      // since auto-pageview is off).
      capture_pageleave: true,
      // Share the anonymous distinct_id across *.mavori.ai so a marketing
      // visitor here becomes the SAME PostHog person when they sign in on
      // app.mavori.ai — first-touch attribution survives into the product.
      cross_subdomain_cookie: true,
      // The product app stores its profile-backed opt-out under this exact
      // cross-subdomain cookie. Landing must read the same consent backend and
      // key before enabling autocapture/session replay; otherwise navigating
      // back to mavori.ai silently restarts collection for an opted-out user.
      opt_out_capturing_persistence_type: "cookie",
      consent_persistence_name: "mavori_analytics_consent",
      // Marketing site: autocapture ON for CTA/click attribution (no financial
      // DOM here).
      autocapture: true,
      // Session replay — LANDING ONLY. Enabled here to watch where visitors
      // bounce; NEVER on the app (balances/orders in the DOM). `maskAllInputs`
      // ensures the waitlist / referral email value is never recorded. Replay
      // is a separate PostHog SKU — set a sampling % in the PostHog project
      // (Settings > Replay) to bound its quota.
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        // rrweb records the initial document href before React can replace a
        // legacy `/referral?lookup=...` URL. PostHog routes both replay hrefs
        // and captured network URLs through this callback.
        maskCapturedNetworkRequestFn: sanitizeLandingCapturedNetworkRequest,
      },
      // Web Vitals (LCP/INP/CLS/FCP) — landing-only CRO + SEO signal. Kept off
      // the app to bound event volume (the app is the higher-traffic surface).
      capture_performance: { web_vitals: true },
      // Rage/dead-click + heatmap/scrollmap data (rides the autocapture already
      // on here) — frustration signals for conversion debugging.
      enable_heatmaps: true,
      sanitize_properties: sanitizeLandingAnalyticsProperties,
      before_send: (event: { properties?: Record<string, unknown> }) => ({
        ...event,
        properties: event.properties
          ? sanitizeLandingAnalyticsProperties(event.properties)
          : event.properties,
      }),
      // No identify() ever fires here, so this keeps every visitor anonymous
      // (no person profiles) — cheapest and privacy-preserving.
      person_profiles: "identified_only",
    });
    client = ph;
    registerAttribution(config.inappSource);
  } catch (err) {
    enabled = false;
    client = null;
    console.warn("[analytics] PostHog init failed", err);
  }
}

// Register the in-app browser source as attribution super properties. Mirrors
// PostHog's own referrer idiom: `initial_inapp_source` is first-touch (never
// overwritten, the attribution field to segment on) and `inapp_source` is the
// current visit. We only register when a source is present so an ordinary
// browser never clobbers a prior first-touch value. UTM + referrer are captured
// automatically by posthog-js and need no wiring here.
function registerAttribution(inappSource?: string): void {
  if (!client || !inappSource) return;
  client.register({ inapp_source: inappSource });
  client.register_once({ initial_inapp_source: inappSource });
}

function suppressed(): boolean {
  return !initialized || !enabled || !client;
}

// Typed to the landing event catalog: callers can only pass a known event name,
// and props are scrubbed to that event's allowlist before egress.
export function captureEvent(
  event: LandingAnalyticsEvent,
  properties?: Record<string, unknown>,
): void {
  if (suppressed() || !client) return;
  client.capture(event, scrubEventProps(event, properties));
}

export function capturePageview(path: string): void {
  if (suppressed() || !client) return;
  client.capture("$pageview", { path });
}
